import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { Leaf, Save, Mail, History, Loader2, RefreshCw, AlertTriangle, Plus, Globe, Pencil, Unlock, ExternalLink, X } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

const fnum = (v) => (v === '' || v == null ? '' : Number(v));

export default function EmissionFactorsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [savingRecips, setSavingRecips] = useState(false);

  const [region, setRegion] = useState('US');
  const [vintage, setVintage] = useState(null);
  const [factors, setFactors] = useState({});
  const [subregions, setSubregions] = useState({});
  const [sources, setSources] = useState({});
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  const [recipients, setRecipients] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/super-admin/emission-factors');
      setData(d);
      setRecipients((d.recipients || []).join(', '));
    } catch {
      toast.error('Failed to load emission factors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const activeSets = data?.active_sets || [];
  const commodityMeta = data?.commodity_meta || {};
  const regionsMeta = data?.regions_meta || {};
  const staleness = data?.staleness || [];

  const regionOptions = useMemo(() => {
    const set = new Set(activeSets.map((s) => s.region));
    ['US', 'AU', 'UK', 'EU', 'CA'].forEach((r) => set.add(r));
    return Array.from(set);
  }, [activeSets]);

  const vintagesForRegion = useMemo(
    () => activeSets.filter((s) => s.region === region).map((s) => s.vintage_year).sort((a, b) => b - a),
    [activeSets, region],
  );

  // Load the selected (region, vintage) set into the editor.
  useEffect(() => {
    if (!activeSets.length) return;
    const vlist = activeSets.filter((s) => s.region === region);
    if (!vlist.length) { setVintage(null); setFactors({}); setSubregions({}); setSources({}); setLabel(''); setSourceUrl(regionsMeta[region]?.source_url || ''); setEditMode(false); return; }
    const chosen = (vintage != null && vlist.find((s) => s.vintage_year === vintage)) || vlist.sort((a, b) => b.vintage_year - a.vintage_year)[0];
    setVintage(chosen.vintage_year);
    setFactors({ ...(chosen.factors || {}) });
    setSubregions(JSON.parse(JSON.stringify(chosen.subregions || {})));
    setSources({ ...(chosen.sources || {}) });
    setLabel(chosen.label || '');
    setSourceUrl(chosen.source_url || regionsMeta[region]?.source_url || '');
    setNote('');
    setEditMode(false);
  }, [activeSets, region, vintage]);  // eslint-disable-line react-hooks/exhaustive-deps

  // The currently selected active set (for the "last updated" line).
  const selectedSet = useMemo(
    () => activeSets.find((s) => s.region === region && s.vintage_year === vintage) || null,
    [activeSets, region, vintage],
  );

  const isUS = region === 'US';
  const commodityRows = Object.entries(commodityMeta).filter(([, m]) => !(m.us_only && !isUS));
  const stateList = regionsMeta[region]?.states || [];

  const saveFactors = async () => {
    if (!vintage) { toast.error('Pick or enter a vintage year first.'); return; }
    setSaving(true);
    try {
      const payload = {
        region,
        vintage_year: Number(vintage),
        factors: Object.fromEntries(
          commodityRows.map(([, m]) => [m.key, fnum(factors[m.key])]).filter(([, v]) => v !== '' && !Number.isNaN(v)),
        ),
        subregions,
        sources,
        label,
        source_url: sourceUrl,
        note,
      };
      await api.put('/super-admin/emission-factors', payload);
      toast.success(`Saved ${region} ${vintage} — new version`);
      setEditMode(false);
      await load();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addVintage = async () => {
    setSaving(true);
    try {
      const { data: r } = await api.post('/super-admin/emission-factors/clone', { region });
      const yr = r.active?.vintage_year;
      toast.success(`Added ${region} ${yr} (cloned from latest)`, { description: 'Update the figures with the official numbers, then Save.' });
      await load();
      setVintage(yr);
      setEditMode(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add vintage');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = async () => {
    await load();       // reload from server → discards unsaved edits
    setEditMode(false);
  };

  const checkUpdates = async () => {
    setChecking(true);
    try {
      const { data: r } = await api.post('/super-admin/emission-factors/check-updates', { region });
      const res = r.result || {};
      if (res.status === 'error') toast.error(res.message);
      else if (res.update_available) toast.warning(res.message, { duration: 9000 });
      else if (res.status === 'unknown') toast.info(res.message, { duration: 8000 });
      else toast.success(res.message);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update check failed');
    } finally {
      setChecking(false);
    }
  };

  const nextVintageYear = Math.max(new Date().getFullYear(), (vintagesForRegion[0] || 0) + 1);

  const saveRecipients = async () => {
    setSavingRecips(true);
    try {
      const list = recipients.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
      await api.put('/super-admin/emission-factors/recipients', { recipients: list });
      toast.success('Recipients updated');
      await load();
    } catch {
      toast.error('Failed to update recipients');
    } finally {
      setSavingRecips(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data: r } = await api.post('/super-admin/emission-factors/notify');
      const res = r.result || {};
      if (res.sent > 0) toast.success(`Assurance email sent to ${res.sent} recipient(s)`);
      else toast.warning(res.configured ? `No emails sent — ${(res.errors || []).join('; ') || 'unknown error'}` : 'Email provider not configured (check Email Config tab)');
      await load();
    } catch {
      toast.error('Failed to send');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 p-8 text-muted-foreground" data-testid="ef-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading emission factors…</div>;
  }

  const staleRows = staleness.filter((s) => s.stale);

  return (
    <div className="space-y-6" data-testid="emission-factors-tab">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-600 text-white"><Leaf className="h-5 w-5" /></span>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Emission Factors</h2>
          <p className="text-xs text-muted-foreground">
            Region + vintage + subregion CO₂e factors. Bills resolve to their region (country/state) and the newest vintage on/before the bill year.
          </p>
        </div>
        <Button variant="ghost" size="sm" className="ml-auto gap-1.5" onClick={load} data-testid="ef-refresh"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
      </div>

      {staleRows.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="ef-staleness">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <b>Check for updates:</b>{' '}
            {staleRows.map((s) => `${s.region} is on data-year ${s.latest_vintage} (${s.years_behind}y old)`).join(' · ')}.
            {' '}Verify a newer official release hasn't been published, then add it as a new vintage.
          </div>
        </div>
      )}

      {/* Region + vintage selector */}
      <div className="border border-border bg-white p-5">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><Globe className="h-4 w-4" /> Region</div>
          <div className="flex gap-1" data-testid="ef-region-tabs">
            {regionOptions.map((r) => (
              <Button key={r} size="sm" variant={region === r ? 'default' : 'outline'} className="h-8 text-xs"
                onClick={() => { setRegion(r); setVintage(null); }} data-testid={`ef-region-${r}`}>
                {regionsMeta[r]?.label || r}
              </Button>
            ))}
          </div>
          <div className="ml-4 flex items-center gap-1.5 text-sm font-semibold text-slate-800">Vintage</div>
          <div className="flex flex-wrap gap-1" data-testid="ef-vintage-tabs">
            {vintagesForRegion.map((v) => (
              <Button key={v} size="sm" variant={vintage === v ? 'default' : 'outline'} className="h-8 text-xs"
                onClick={() => setVintage(v)} data-testid={`ef-vintage-${v}`}>
                {v}
              </Button>
            ))}
            {vintage != null && !vintagesForRegion.includes(vintage) && (
              <Button size="sm" variant="default" className="h-8 text-xs" data-testid={`ef-vintage-${vintage}`}>{vintage} (new)</Button>
            )}
            <Button size="sm" variant="ghost" className="h-8 gap-1 text-xs text-emerald-700" onClick={addVintage} disabled={saving} data-testid="ef-add-vintage">
              <Plus className="h-3.5 w-3.5" /> Add {nextVintageYear} (clone latest)
            </Button>
          </div>
        </div>

        {/* Last-updated + official source + edit lock */}
        <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-slate-100 bg-slate-50/60 px-3 py-2 text-xs" data-testid="ef-meta">
          <span className="text-slate-600" data-testid="ef-last-updated">
            {selectedSet ? (
              <>Last updated <b>{selectedSet.created_at ? new Date(selectedSet.created_at).toLocaleDateString() : '—'}</b> by <b>{selectedSet.created_by || 'system'}</b>{selectedSet.version ? ` · v${selectedSet.version}` : ''}</>
            ) : 'No active set for this region/vintage yet.'}
          </span>
          {(sourceUrl || regionsMeta[region]?.source_url) && (
            <a href={sourceUrl || regionsMeta[region]?.source_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 font-medium text-emerald-700 hover:underline" data-testid="ef-source-link">
              <ExternalLink className="h-3 w-3" /> Official source
            </a>
          )}
          {data?.last_checked_at && (
            <span className="text-slate-400">Last checked {new Date(data.last_checked_at).toLocaleDateString()}</span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={checkUpdates} disabled={checking} data-testid="ef-check-updates-btn">
              {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Check for updates
            </Button>
            {!editMode ? (
              <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setEditMode(true)} data-testid="ef-edit-btn">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
            ) : (
              <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700" data-testid="ef-editing-badge">
                <Unlock className="h-3 w-3" /> Editing — remember to Save
              </span>
            )}
          </div>
        </div>

        {/* Result of the last on-demand update check for this region */}
        {(data?.update_checks || {})[region] && (
          <div
            className={`-mt-2 mb-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${
              (data.update_checks[region].update_available)
                ? 'border-amber-200 bg-amber-50 text-amber-800'
                : data.update_checks[region].status === 'error'
                ? 'border-rose-200 bg-rose-50 text-rose-700'
                : 'border-emerald-200 bg-emerald-50 text-emerald-800'
            }`}
            data-testid="ef-update-check-result"
          >
            {data.update_checks[region].update_available
              ? <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              : <RefreshCw className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
            <span>
              {data.update_checks[region].message}
              {data.update_checks[region].checked_at && (
                <span className="ml-1 text-[10px] opacity-70">
                  (checked {new Date(data.update_checks[region].checked_at).toLocaleString()})
                </span>
              )}
            </span>
          </div>
        )}

        <fieldset disabled={!editMode} className="m-0 border-0 p-0 disabled:opacity-70" data-testid="ef-editor-fieldset">
        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <Label className="text-xs">Vintage (data) year</Label>
            <Input type="number" value={vintage ?? ''} onChange={(e) => setVintage(e.target.value ? Number(e.target.value) : null)} data-testid="ef-vintage-input" />
          </div>
          <div>
            <Label className="text-xs">Label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. United States — EPA eGRID 2024" data-testid="ef-label" />
          </div>
        </div>

        <div className="mb-4">
          <Label className="text-xs">Official source URL</Label>
          <Input value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="https://www.epa.gov/egrid" data-testid="ef-source-url" />
          <span className="text-[10px] text-muted-foreground">Where these factors were sourced — shown &amp; linked to reviewers and in the reminder email.</span>
        </div>

        {/* Commodity default factors */}
        <h3 className="mb-3 text-sm font-semibold text-slate-800">Region default factors</h3>
        <div className="space-y-3">
          {commodityRows.map(([key, m]) => (
            <div key={key} className="grid grid-cols-1 gap-2 md:grid-cols-[200px_170px_1fr] md:items-center" data-testid={`ef-row-${key}`}>
              <Label className="font-medium text-slate-700">{m.label}{m.subregional && <span className="ml-1 text-[10px] font-normal text-emerald-600">· subregional</span>}</Label>
              <div>
                <Input type="number" step="any" value={factors[m.key] ?? ''}
                  onChange={(e) => setFactors((f) => ({ ...f, [m.key]: e.target.value }))} data-testid={`ef-input-${key}`} />
                <span className="text-[10px] text-muted-foreground">{m.unit}</span>
              </div>
              <Textarea rows={1} placeholder="Source / citation" value={sources[key] ?? ''}
                onChange={(e) => setSources((s) => ({ ...s, [key]: e.target.value }))} className="text-xs" data-testid={`ef-source-${key}`} />
            </div>
          ))}
        </div>

        {/* Subregion electricity overrides */}
        <h3 className="mb-2 mt-6 text-sm font-semibold text-slate-800">Sub-region electricity factors <span className="text-[11px] font-normal text-muted-foreground">(kg CO₂e / kWh — overrides the region default per state)</span></h3>
        <div className="max-h-[280px] overflow-y-auto rounded border border-slate-100" data-testid="ef-subregions">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 p-3 md:grid-cols-3">
            {stateList.map((st) => (
              <div key={st} className="flex items-center gap-2">
                <span className="w-12 shrink-0 text-xs font-mono text-slate-600">{st}</span>
                <Input
                  type="number" step="any" className="h-8 text-xs"
                  value={subregions[st]?.electricity_kg_per_kwh ?? ''}
                  onChange={(e) => setSubregions((s) => ({ ...s, [st]: { ...(s[st] || {}), electricity_kg_per_kwh: e.target.value } }))}
                  data-testid={`ef-sub-${st}`}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-xs">Change note (optional)</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. eGRID 2024 update" data-testid="ef-note" />
        </div>
        </fieldset>

        {editMode ? (
          <div className="mt-4 flex items-center gap-2">
            <Button className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" onClick={saveFactors} disabled={saving} data-testid="ef-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save {region} {vintage} — new version
            </Button>
            <Button variant="outline" className="gap-1.5" onClick={cancelEdit} disabled={saving} data-testid="ef-cancel-btn">
              <X className="h-4 w-4" /> Cancel
            </Button>
          </div>
        ) : (
          <p className="mt-4 text-xs text-muted-foreground" data-testid="ef-locked-hint">
            🔒 Locked to prevent accidental changes. Click <b>Edit</b> to modify, then Save (creates a new version, recording your name).
          </p>
        )}
      </div>

      {/* Monthly assurance email */}
      <div className="border border-border bg-white p-5">
        <h3 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-slate-800"><Mail className="h-4 w-4" /> Monthly assurance email</h3>
        <p className="mb-3 text-xs text-muted-foreground">Sent ~monthly listing all region factor sets and flagging any region that is behind on its published vintage.</p>
        <Label className="text-xs">Recipients (comma or newline separated)</Label>
        <Textarea rows={2} value={recipients} onChange={(e) => setRecipients(e.target.value)} className="text-xs" data-testid="ef-recipients" />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={saveRecipients} disabled={savingRecips} data-testid="ef-save-recipients">
            {savingRecips ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save recipients
          </Button>
          <Button size="sm" className="gap-1.5" onClick={sendNow} disabled={sending} data-testid="ef-send-now">
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Send now
          </Button>
          {data?.next_notify_at && <span className="text-[11px] text-muted-foreground">Next auto-send: {new Date(data.next_notify_at).toLocaleDateString()}</span>}
        </div>
        {data?.last_notify_result && (
          <div className="mt-3 rounded border border-slate-100 bg-slate-50 p-2 text-[11px] text-slate-600" data-testid="ef-last-result">
            Last {data.last_notify_result.triggered_by || 'run'} · {data.last_notify_result.at ? new Date(data.last_notify_result.at).toLocaleString() : ''} · sent {data.last_notify_result.sent} · env {data.last_notify_result.env}
            {(data.last_notify_result.errors || []).length > 0 && <span className="text-amber-600"> · {data.last_notify_result.errors.join('; ')}</span>}
          </div>
        )}
      </div>

      {/* Version history */}
      <div className="border border-border bg-white p-5">
        <h3 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-slate-800"><History className="h-4 w-4" /> Version history</h3>
        <div className="space-y-1.5" data-testid="ef-history">
          {(data?.versions || []).map((v) => (
            <div key={`${v.region}-${v.vintage_year}-${v.version}`} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-xs last:border-0">
              <span className="font-mono">{v.region} {v.vintage_year} · v{v.version} {v.is_active && <span className="ml-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">ACTIVE</span>}</span>
              <span className="font-mono text-slate-500">e {v.factors?.electricity_kg_per_kwh} · g {v.factors?.natural_gas_kg_per_therm} · w {v.factors?.water_kg_per_gallon}</span>
              <span className="text-slate-400">{v.created_by} · {v.created_at ? new Date(v.created_at).toLocaleDateString() : ''}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
